/**
 * 肌年齢換算モデル。
 *
 * ねらい：UV家計簿を「プール○時間ぶん」より生活に近い「肌年齢が何歳ぶん進んだか」で
 * 表す。ただし **写真から肌年齢を測ることはしない**。検証された手法が無く、
 * 数字を作れば嘘になるためである。
 *
 * 代わりに次の構成をとる：
 *   1. 肌年齢は **外部の測定器の実測値**（店頭の肌年齢測定など）を入力してもらう
 *   2. 実測値と、その間に浴びたUV・大気汚染の累積から **係数を最小二乗で推定** する
 *   3. 校正が済むまでは暫定係数を使い、「暫定」であることを画面に出す
 *
 * この「初期値は暫定、校正で確定、確定前の値を精度の対外主張に使わない」という
 * 進め方は設計書§15の校正工程と同じ流儀である。
 *
 * ⚠️ 薬機法・SaMD回避（§1.3）：肌年齢は美容領域の目安であって医学的診断ではない。
 * 疾病の予防・診断をうたわず、あくまで自分の記録の見方として提示する。
 */

/**
 * 【初期値・要校正】UV 1 SED あたり肌年齢が進む年数。
 * 一般的な生活者の年間UV曝露（およそ150 SED）で肌年齢が1歳ぶん余分に進む、という
 * 桁合わせの置き方であり、文献値ではない。実測が3件たまれば置き換わる。
 */
export const SKIN_AGE_YEARS_PER_SED = 1 / 150;

/**
 * 【初期値・要校正】PM2.5 の曝露（μg/m³·日）あたり肌年齢が進む年数。
 * 大気汚染と肌の色素沈着の関連は疫学的に報告があるが、換算係数は確立していない。
 * 実測が6件たまるまでは、この暫定値を固定で使う（少ない点で2変数を当てると暴れるため）。
 */
export const SKIN_AGE_YEARS_PER_PM25_DAY = 1 / 30000;

/** 校正に必要な最小の実測件数 */
export const MIN_READINGS_FOR_UV_FIT = 3;
/** 大気汚染の係数も一緒に推定するのに必要な件数 */
export const MIN_READINGS_FOR_PM_FIT = 6;

/** 店頭の測定器などで測った肌年齢の記録 */
export interface SkinAgeReading {
  ts: number;
  /** どのユーザーの測定値か（端末内アカウントのid、accounts.ts） */
  userId?: string;
  /** 測定された肌年齢 [歳] */
  measuredAge: number;
  /** そのときの実年齢 [歳] */
  chronologicalAge: number;
  /** 測定元（例：「無印良品 店頭測定」）。どの機種かで系統差が出るため残す */
  source: string;
}

/** 実測時点までの累積曝露。呼び出し側が日次集計から作る */
export interface ExposureSnapshot {
  ts: number;
  /** 累積UV [SED] */
  cumSed: number;
  /** 累積PM2.5曝露 [μg/m³·日] */
  cumPm25Day: number;
}

export interface SkinAgeModel {
  /** UV 1 SED あたりの年数 */
  yearsPerSed: number;
  /** PM2.5曝露あたりの年数 */
  yearsPerPm25Day: number;
  /** 切片（実年齢との差の基準値）[歳] */
  offsetYears: number;
  /** 校正済みか（false なら暫定係数） */
  calibrated: boolean;
  /** 校正に使った実測件数 */
  sampleCount: number;
  /** 当てはまりの良さ 0..1（校正済みのときのみ意味を持つ） */
  r2: number;
  /** 大気汚染の係数も推定したか */
  pmFitted: boolean;
}

export const UNCALIBRATED_MODEL: SkinAgeModel = {
  yearsPerSed: SKIN_AGE_YEARS_PER_SED,
  yearsPerPm25Day: SKIN_AGE_YEARS_PER_PM25_DAY,
  offsetYears: 0,
  calibrated: false,
  sampleCount: 0,
  r2: 0,
  pmFitted: false,
};

/** 正規方程式をガウス消去で解く（小さい行列のみ想定） */
function solve(a: number[][], b: number[]): number[] | null {
  const n = b.length;
  const m = a.map((row, i) => [...row, b[i]!]);
  for (let col = 0; col < n; col++) {
    let pivot = col;
    for (let r = col + 1; r < n; r++) {
      if (Math.abs(m[r]![col]!) > Math.abs(m[pivot]![col]!)) pivot = r;
    }
    if (Math.abs(m[pivot]![col]!) < 1e-12) return null; // 特異＝説明変数に変化がない
    [m[col], m[pivot]] = [m[pivot]!, m[col]!];
    for (let r = 0; r < n; r++) {
      if (r === col) continue;
      const f = m[r]![col]! / m[col]![col]!;
      for (let c = col; c <= n; c++) m[r]![c]! -= f * m[col]![c]!;
    }
  }
  return m.map((row, i) => row[n]! / row[i]!);
}

/** 最小二乗フィット。X の各行が説明変数、y が目的変数 */
function leastSquares(X: number[][], y: number[]): { coef: number[]; r2: number } | null {
  const k = X[0]?.length ?? 0;
  if (k === 0 || X.length < k) return null;
  const ata: number[][] = Array.from({ length: k }, () => new Array<number>(k).fill(0));
  const atb: number[] = new Array<number>(k).fill(0);
  for (let i = 0; i < X.length; i++) {
    for (let r = 0; r < k; r++) {
      atb[r]! += X[i]![r]! * y[i]!;
      for (let c = 0; c < k; c++) ata[r]![c]! += X[i]![r]! * X[i]![c]!;
    }
  }
  const coef = solve(ata, atb);
  if (!coef) return null;

  const mean = y.reduce((s, v) => s + v, 0) / y.length;
  let ssRes = 0;
  let ssTot = 0;
  for (let i = 0; i < X.length; i++) {
    const pred = X[i]!.reduce((s, v, j) => s + v * coef[j]!, 0);
    ssRes += (y[i]! - pred) ** 2;
    ssTot += (y[i]! - mean) ** 2;
  }
  return { coef, r2: ssTot > 1e-12 ? Math.max(0, 1 - ssRes / ssTot) : 1 };
}

/**
 * 実測の肌年齢からモデルを推定する。
 * 目的変数は「肌年齢 − 実年齢」（＝実年齢からのずれ）で、これを累積曝露で説明する。
 * 件数が足りない・説明変数に変化が無い場合は暫定係数のまま返す。
 */
export function fitSkinAgeModel(
  readings: SkinAgeReading[],
  exposureAt: (ts: number) => ExposureSnapshot,
): SkinAgeModel {
  const sorted = [...readings].sort((a, b) => a.ts - b.ts);
  if (sorted.length < MIN_READINGS_FOR_UV_FIT) {
    return { ...UNCALIBRATED_MODEL, sampleCount: sorted.length };
  }

  const samples = sorted.map((r) => ({ ...exposureAt(r.ts), delta: r.measuredAge - r.chronologicalAge }));
  const y = samples.map((s) => s.delta);
  const fitPm = sorted.length >= MIN_READINGS_FOR_PM_FIT;

  // 汚染も推定するなら [cumSed, cumPm, 1]、しないなら [cumSed, 1]（汚染分は暫定係数で差し引く）
  const X = fitPm
    ? samples.map((s) => [s.cumSed, s.cumPm25Day, 1])
    : samples.map((s) => [s.cumSed, 1]);
  const yAdj = fitPm
    ? y
    : y.map((v, i) => v - samples[i]!.cumPm25Day * SKIN_AGE_YEARS_PER_PM25_DAY);

  const fit = leastSquares(X, yAdj);
  if (!fit) return { ...UNCALIBRATED_MODEL, sampleCount: sorted.length };

  const yearsPerSed = fit.coef[0]!;
  // 負の係数（浴びるほど若返る）は物理的に意味を成さないので採用しない
  if (!Number.isFinite(yearsPerSed) || yearsPerSed < 0) {
    return { ...UNCALIBRATED_MODEL, sampleCount: sorted.length };
  }

  return {
    yearsPerSed,
    yearsPerPm25Day: fitPm ? Math.max(0, fit.coef[1]!) : SKIN_AGE_YEARS_PER_PM25_DAY,
    offsetYears: fitPm ? fit.coef[2]! : fit.coef[1]!,
    calibrated: true,
    sampleCount: sorted.length,
    r2: fit.r2,
    pmFitted: fitPm,
  };
}

/** 曝露から肌年齢の進み [歳] を見積もる */
export function estimateSkinAgeYears(
  model: SkinAgeModel,
  exposure: { cumSed: number; cumPm25Day: number },
): number {
  return exposure.cumSed * model.yearsPerSed + exposure.cumPm25Day * model.yearsPerPm25Day;
}

export interface SkinAgeChange {
  /** 実測の肌年齢の変化 [歳]（負なら若返り）。実測2件以上で得られる */
  measuredYears: number | null;
  /** UV・大気汚染で進んだと見込まれる分 [歳] */
  exposureYears: number;
  /**
   * 実測と曝露の差 [歳]。ケアや生活の影響として現れる分。
   * 負なら「曝露のわりに進んでいない＝ケアが効いている可能性」。
   * 因果は主張せず、差として示すに留める。
   */
  careYears: number | null;
}

/**
 * 期間中の肌年齢の増減を、曝露で説明できる分とそれ以外に分解する。
 * 実測が2件無いときは曝露からの見積もりのみ返す。
 */
export function decomposeSkinAgeChange(
  model: SkinAgeModel,
  readings: SkinAgeReading[],
  exposure: { cumSed: number; cumPm25Day: number },
): SkinAgeChange {
  const exposureYears = estimateSkinAgeYears(model, exposure);
  const sorted = [...readings].sort((a, b) => a.ts - b.ts);
  if (sorted.length < 2) {
    return { measuredYears: null, exposureYears, careYears: null };
  }
  const first = sorted[0]!;
  const last = sorted[sorted.length - 1]!;
  // 実年齢の進みぶんを除いた、肌年齢のずれの変化
  const measuredYears =
    last.measuredAge - last.chronologicalAge - (first.measuredAge - first.chronologicalAge);
  return { measuredYears, exposureYears, careYears: measuredYears - exposureYears };
}

/** 表示用の符号つき文字列（例：「+0.3歳」「−0.2歳」） */
export function formatSkinAgeYears(years: number): string {
  const rounded = Math.abs(years) < 0.05 ? 0 : years;
  if (rounded === 0) return "±0歳";
  const sign = rounded > 0 ? "+" : "−";
  return `${sign}${Math.abs(rounded).toFixed(1)}歳`;
}

/** 校正状況の説明文（暫定値であることを隠さない） */
export function calibrationNote(model: SkinAgeModel): string {
  if (!model.calibrated) {
    return (
      `肌年齢への換算はまだ暫定値です（実測 ${model.sampleCount}/${MIN_READINGS_FOR_UV_FIT} 件）。` +
      `店頭などで測った肌年齢を登録すると、あなたのデータで係数を校正します。`
    );
  }
  const pm = model.pmFitted ? "・大気汚染" : "";
  return `あなたの実測${model.sampleCount}件で校正済み（UV${pm}、当てはまり R²=${model.r2.toFixed(2)}）`;
}
